import { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import type { Employee, SkillLevel } from "../types";
import { SKILL_LEVEL_LABELS, SKILL_LEVEL_COLORS } from "../types";
import { useSkills, useSkillsActions, useEmployees, useEmployeesActions } from "../stores";
import { SkillLevelSelector, AddSkillModal } from "./skills";
import { SkillPresetsPanel } from "./skills/SkillPresetsPanel";
import { AddEmployeeModal } from "../employees/components";
import { PageContainer } from "./PageLayout";
import { parseSkillsFromEmojis, skillsToEmojiString } from "../lib/skillEngine";
import type { SkillDefinition } from "../skills";
import { Sparkles, ChevronDown, Edit2, Trash2, Plus, Users, X, GripVertical } from "lucide-react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from "@dnd-kit/core";
import type { DragStartEvent, DragEndEvent } from "@dnd-kit/core";

// ==========================================
// Draggable Employee Row
// ==========================================

function DraggableEmployeeRow({
  employee,
  skillId,
  level,
  onChangeLevel,
  onRemove,
}: {
  employee: Employee;
  skillId: string;
  level: SkillLevel;
  onChangeLevel: (level: SkillLevel) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${skillId}::${employee.id}`,
    data: { employeeId: employee.id, fromSkillId: skillId, level },
  });

  return (
    <div
      ref={setNodeRef}
      className={`flex items-center justify-between gap-2 px-2 py-1 rounded hover:bg-gray-100 transition-colors group ${
        isDragging ? "opacity-30" : ""
      }`}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <button
          {...listeners}
          {...attributes}
          className="p-0.5 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
          title={t('skills.dragToMoveSkill')}
        >
          <GripVertical size={12} />
        </button>
        <div
          className="w-2.5 h-2.5 rounded-full shrink-0"
          style={{ backgroundColor: employee.color }}
        />
        <span className="text-xs text-gray-700 truncate">{employee.name}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${SKILL_LEVEL_COLORS[level]} border`}>
          {SKILL_LEVEL_LABELS[level]}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <SkillLevelSelector
          level={level}
          onChange={onChangeLevel}
          compact
        />
        <button
          onClick={onRemove}
          className="p-0.5 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all shrink-0"
          title={t('skills.removeAssignment')}
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

// ==========================================
// Droppable Skill Area
// ==========================================

function DroppableSkillArea({
  skillId,
  children,
  isOver,
}: {
  skillId: string;
  children: React.ReactNode;
  isOver: boolean;
}) {
  const { setNodeRef } = useDroppable({ id: `drop::${skillId}` });

  return (
    <div
      ref={setNodeRef}
      className={`bg-gray-50 border-t border-gray-100 transition-colors ${
        isOver ? "bg-blue-50 ring-1 ring-inset ring-blue-200" : ""
      }`}
    >
      {children}
    </div>
  );
}

// ==========================================
// Drag Overlay Content
// ==========================================

function DragOverlayContent({ employee, level }: { employee: Employee; level: SkillLevel }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded bg-white shadow-lg border border-gray-200 w-fit">
      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: employee.color }} />
      <span className="text-xs font-medium text-gray-900">{employee.name}</span>
      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${SKILL_LEVEL_COLORS[level]} border`}>
        {SKILL_LEVEL_LABELS[level]}
      </span>
    </div>
  );
}

// ==========================================
// Add Employee Dropdown (inline)
// ==========================================

function AddEmployeeDropdown({
  unassignedEmployees,
  onAssign,
  onAddNew,
}: {
  unassignedEmployees: Employee[];
  onAssign: (employeeId: string) => void;
  onAddNew: () => void;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 px-2 py-1 text-[11px] text-gray-400 hover:text-gray-600 transition-colors w-full"
      >
        <Plus size={12} />
        {t('employees.assignEmployee')}
      </button>
      {isOpen && (
        <div className="absolute left-0 top-full mt-1 z-20 bg-white rounded border border-gray-200 shadow-lg min-w-[200px] max-h-[200px] overflow-y-auto">
          {unassignedEmployees.length === 0 ? (
            <div className="px-3 py-2">
              <p className="text-[11px] text-gray-400 mb-1.5">{t('employees.allAssigned')}</p>
              <button
                onClick={() => { onAddNew(); setIsOpen(false); }}
                className="flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-700"
              >
                <Plus size={12} />
                {t('employees.createNew')}
              </button>
            </div>
          ) : (
            <>
              {unassignedEmployees.map((emp) => (
                <button
                  key={emp.id}
                  onClick={() => { onAssign(emp.id); setIsOpen(false); }}
                  className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: emp.color }} />
                  <span className="text-xs text-gray-700">{emp.name}</span>
                </button>
              ))}
              <div className="border-t border-gray-100">
                <button
                  onClick={() => { onAddNew(); setIsOpen(false); }}
                  className="flex items-center gap-1 w-full px-3 py-1.5 text-[11px] text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  <Plus size={12} />
                  {t('employees.createNew')}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ==========================================
// Main Component
// ==========================================

interface SkillEngineProps {
  employees?: Employee[];
}

export function SkillEngine({ employees: propsEmployees }: SkillEngineProps) {
  const { t } = useTranslation();
  const storeEmployees = useEmployees();
  const employees = propsEmployees ?? storeEmployees;
  const { addNewEmployee, updateEmployee } = useEmployeesActions();

  const skillDefinitions = useSkills();
  const { addSkill, addNewSkill, updateSkill: updateSkillDefinition, deleteSkill: deleteSkillDefinition } = useSkillsActions();

  // Derive employee skills directly from store — single source of truth, no local state
  const employeeSkillsMap = useMemo(() => {
    const map: Record<string, Record<string, SkillLevel>> = {};
    for (const emp of employees) {
      map[emp.id] = parseSkillsFromEmojis(emp.skills);
    }
    return map;
  }, [employees, skillDefinitions]); // eslint-disable-line react-hooks/exhaustive-deps

  // UI State
  const [showAddSkillModal, setShowAddSkillModal] = useState(false);
  const [showPresetsPanel, setShowPresetsPanel] = useState(false);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [expandedSkills, setExpandedSkills] = useState<Set<string>>(new Set());
  const [editingSkill, setEditingSkill] = useState<SkillDefinition | null>(null);
  const [deletingSkillId, setDeletingSkillId] = useState<string | null>(null);

  // DnD State
  const [draggedInfo, setDraggedInfo] = useState<{
    employeeId: string;
    fromSkillId: string;
    level: SkillLevel;
  } | null>(null);
  const [overSkillId, setOverSkillId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Update a single skill level for an employee — writes directly to store
  const updateSkillLevel = (employeeId: string, skillId: string, level: SkillLevel) => {
    const currentSkills = { ...employeeSkillsMap[employeeId] };
    currentSkills[skillId] = level;
    updateEmployee(employeeId, { skills: skillsToEmojiString(currentSkills) });
  };

  // Batch update: set multiple skill changes for one employee at once
  const updateMultipleSkillLevels = (employeeId: string, changes: Record<string, SkillLevel>) => {
    const currentSkills = { ...employeeSkillsMap[employeeId] };
    for (const [skillId, level] of Object.entries(changes)) {
      currentSkills[skillId] = level;
    }
    updateEmployee(employeeId, { skills: skillsToEmojiString(currentSkills) });
  };

  const existingSkillIds = useMemo(
    () => new Set(skillDefinitions.map((s) => s.id)),
    [skillDefinitions]
  );

  const handleAddPresetSkills = (skills: SkillDefinition[]) => {
    for (const s of skills) {
      addSkill(s);
    }
  };

  const toggleExpand = (skillId: string) => {
    setExpandedSkills((prev) => {
      const next = new Set(prev);
      if (next.has(skillId)) next.delete(skillId);
      else next.add(skillId);
      return next;
    });
  };

  const handleDeleteSkill = (skillId: string) => {
    deleteSkillDefinition(skillId);
    setDeletingSkillId(null);
    setExpandedSkills((prev) => {
      const next = new Set(prev);
      next.delete(skillId);
      return next;
    });
  };

  const handleUpdateSkill = (skill: SkillDefinition) => {
    updateSkillDefinition(skill.id, {
      name: skill.name,
      emoji: skill.emoji,
      description: skill.description,
    });
  };

  const removeEmployeeFromSkill = (employeeId: string, skillId: string) => {
    updateSkillLevel(employeeId, skillId, 0);
  };

  const assignEmployeeToSkill = (employeeId: string, skillId: string, level: SkillLevel = 1) => {
    updateSkillLevel(employeeId, skillId, level);
    setExpandedSkills((prev) => new Set(prev).add(skillId));
  };

  // DnD: Move employee from source skill to target skill
  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as {
      employeeId: string;
      fromSkillId: string;
      level: SkillLevel;
    };
    setDraggedInfo(data);
  };

  const handleDragOver = (event: { over: { id: string | number } | null }) => {
    if (!event.over) {
      setOverSkillId(null);
      return;
    }
    const overId = String(event.over.id);
    if (overId.startsWith("drop::")) {
      setOverSkillId(overId.replace("drop::", ""));
    } else {
      setOverSkillId(null);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { over } = event;
    if (draggedInfo && over) {
      const overId = String(over.id);
      if (overId.startsWith("drop::")) {
        const targetSkillId = overId.replace("drop::", "");
        if (targetSkillId !== draggedInfo.fromSkillId) {
          // MOVE: remove from source, add to target (same level)
          updateMultipleSkillLevels(draggedInfo.employeeId, {
            [draggedInfo.fromSkillId]: 0,
            [targetSkillId]: draggedInfo.level,
          });
          setExpandedSkills((prev) => new Set(prev).add(targetSkillId));
        }
      }
    }
    setDraggedInfo(null);
    setOverSkillId(null);
  };

  const handleDragCancel = () => {
    setDraggedInfo(null);
    setOverSkillId(null);
  };

  const hasSkills = skillDefinitions.length > 0;
  const hasEmployees = employees.length > 0;

  const getAssignedEmployees = (skillId: string) => {
    return employees
      .map((emp) => {
        const level = (employeeSkillsMap[emp.id]?.[skillId] ?? 0) as SkillLevel;
        return { employee: emp, level };
      })
      .filter((e) => e.level > 0)
      .sort((a, b) => b.level - a.level);
  };

  const getUnassignedEmployees = (skillId: string) => {
    return employees.filter((emp) => {
      return (employeeSkillsMap[emp.id]?.[skillId] ?? 0) === 0;
    });
  };

  const draggedEmployee = draggedInfo
    ? employees.find((e) => e.id === draggedInfo.employeeId)
    : null;

  return (
    <PageContainer wide>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-gray-900">{t('skills.title')}</h1>
          <p className="text-xs text-gray-500">
            {hasSkills
              ? t('skills.subtitle', { count: skillDefinitions.length, employeeCount: employees.length })
              : t('skills.subtitleEmpty')}
          </p>
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={() => setShowAddEmployeeModal(true)}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded px-2.5 py-1 transition-colors"
          >
            <Users size={14} />
            {t('employees.title')}
          </button>
          <button
            onClick={() => setShowPresetsPanel(true)}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded px-2.5 py-1 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            {t('skills.fromPreset')}
          </button>
          <button
            onClick={() => { setEditingSkill(null); setShowAddSkillModal(true); }}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded px-2.5 py-1 transition-colors"
          >
            <Plus size={14} />
            {t('skills.addSkillButton')}
          </button>
        </div>
      </div>

      {/* Empty State: No skills AND no employees */}
      {!hasSkills && !hasEmployees && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 mb-3">
            <Sparkles size={20} />
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">{t('skills.emptyNoSkillsNoEmployees')}</h3>
          <p className="text-xs text-gray-500 mb-4">{t('skills.emptyNoSkillsNoEmployeesDesc')}</p>
          <div className="flex gap-2">
            <button
              onClick={() => setShowAddEmployeeModal(true)}
              className="px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
            >
              {t('employees.addButton')}
            </button>
            <button
              onClick={() => setShowPresetsPanel(true)}
              className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 hover:bg-gray-50 rounded transition-colors"
            >
              {t('skills.fromPreset')}
            </button>
          </div>
        </div>
      )}

      {/* Empty State: No skills but has employees */}
      {!hasSkills && hasEmployees && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 mb-3">
            <Sparkles size={20} />
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">{t('skills.emptyNoSkills')}</h3>
          <p className="text-xs text-gray-500 mb-4">{t('skills.emptyNoSkillsDesc')}</p>
          <div className="flex gap-2">
            <button
              onClick={() => setShowPresetsPanel(true)}
              className="px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
            >
              {t('skills.addFromPreset')}
            </button>
            <button
              onClick={() => { setEditingSkill(null); setShowAddSkillModal(true); }}
              className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 hover:bg-gray-50 rounded transition-colors"
            >
              {t('skills.singleSkill')}
            </button>
          </div>
        </div>
      )}

      {/* Skill List with DnD */}
      {hasSkills && (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="bg-white border border-gray-200 rounded overflow-hidden">
            <div className="divide-y divide-gray-100">
              {skillDefinitions.map((skill) => {
                const isExpanded = expandedSkills.has(skill.id);
                const isDeleting = deletingSkillId === skill.id;
                const assignedEmployees = getAssignedEmployees(skill.id);
                const unassignedEmployees = getUnassignedEmployees(skill.id);
                const isDropTarget = overSkillId === skill.id && draggedInfo?.fromSkillId !== skill.id;

                return (
                  <div key={skill.id}>
                    {/* Skill Row */}
                    <div className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 transition-colors">
                      <button
                        onClick={() => toggleExpand(skill.id)}
                        className="p-0.5 text-gray-400 hover:text-gray-600 transition-colors"
                        title={isExpanded ? t('skills.collapse') : t('skills.expand')}
                      >
                        <ChevronDown
                          size={14}
                          className={`transition-transform ${isExpanded ? "" : "-rotate-90"}`}
                        />
                      </button>
                      <span className="text-sm">{skill.emoji}</span>
                      <button
                        onClick={() => toggleExpand(skill.id)}
                        className="flex-1 text-left min-w-0"
                      >
                        <span className="text-xs font-medium text-gray-900">{skill.name}</span>
                        {skill.description && (
                          <span className="text-[10px] text-gray-400 ml-2">{skill.description}</span>
                        )}
                      </button>
                      {assignedEmployees.length > 0 && (
                        <span className="text-[10px] text-gray-400 tabular-nums shrink-0">
                          {t('skills.employeesWithSkill', { count: assignedEmployees.length })}
                        </span>
                      )}
                      <button
                        onClick={() => { setEditingSkill(skill); setShowAddSkillModal(true); }}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                        title={t('common.edit')}
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => setDeletingSkillId(isDeleting ? null : skill.id)}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title={t('common.delete')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    {/* Delete Confirmation */}
                    {isDeleting && (
                      <div className="px-3 py-2 bg-red-50 border-t border-red-100">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-red-700">
                            {t('skills.deleteConfirm', { name: skill.name })}
                          </span>
                          <div className="flex gap-1.5">
                            <button
                              onClick={() => setDeletingSkillId(null)}
                              className="px-2 py-0.5 text-[10px] text-gray-600 hover:bg-white rounded transition-colors"
                            >
                              {t('common.cancel')}
                            </button>
                            <button
                              onClick={() => handleDeleteSkill(skill.id)}
                              className="px-2 py-0.5 text-[10px] text-red-600 font-medium hover:bg-red-100 rounded transition-colors"
                            >
                              {t('common.delete')}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Expanded / Drop Target: Assigned Employees */}
                    {(isExpanded || isDropTarget) && (
                      <DroppableSkillArea skillId={skill.id} isOver={isDropTarget}>
                        <div className="px-3 py-2 space-y-0.5">
                          {assignedEmployees.length === 0 && !isDropTarget && (
                            <p className="text-[10px] text-gray-400 py-1 px-2">
                              {t('skills.noEmployeesAssigned')}
                            </p>
                          )}
                          {isDropTarget && assignedEmployees.length === 0 && (
                            <p className="text-[11px] text-blue-500 py-1 px-2 font-medium">
                              {t('skills.dropToAssign')}
                            </p>
                          )}
                          {assignedEmployees.map(({ employee, level }) => (
                            <DraggableEmployeeRow
                              key={employee.id}
                              employee={employee}
                              skillId={skill.id}
                              level={level}
                              onChangeLevel={(newLevel) => {
                                if (newLevel === 0) {
                                  removeEmployeeFromSkill(employee.id, skill.id);
                                } else {
                                  updateSkillLevel(employee.id, skill.id, newLevel);
                                }
                              }}
                              onRemove={() => removeEmployeeFromSkill(employee.id, skill.id)}
                            />
                          ))}
                          {isExpanded && (
                            <AddEmployeeDropdown
                              unassignedEmployees={unassignedEmployees}
                              onAssign={(empId) => assignEmployeeToSkill(empId, skill.id)}
                              onAddNew={() => setShowAddEmployeeModal(true)}
                            />
                          )}
                        </div>
                      </DroppableSkillArea>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Drag Overlay */}
          <DragOverlay>
            {draggedInfo && draggedEmployee && (
              <DragOverlayContent
                employee={draggedEmployee}
                level={draggedInfo.level}
              />
            )}
          </DragOverlay>
        </DndContext>
      )}

      {/* Skill Modal (Add / Edit) */}
      <AddSkillModal
        isOpen={showAddSkillModal}
        onClose={() => { setShowAddSkillModal(false); setEditingSkill(null); }}
        onAdd={addNewSkill}
        editSkill={editingSkill}
        onUpdate={handleUpdateSkill}
      />

      {/* Add Employee Modal */}
      <AddEmployeeModal
        isOpen={showAddEmployeeModal}
        onClose={() => setShowAddEmployeeModal(false)}
        onAdd={addNewEmployee}
      />

      {/* Presets Panel */}
      <SkillPresetsPanel
        isOpen={showPresetsPanel}
        onClose={() => setShowPresetsPanel(false)}
        onAddSkills={handleAddPresetSkills}
        existingSkillIds={existingSkillIds}
      />
    </PageContainer>
  );
}
